/** Az irodai landing publikus adatai: ezt adja ki az `iroda-oldal` Edge Function, és ezt rendereli az Astro. */
export interface LandingKollega { nev: string; szerep?: string; fotoUrl?: string }
export interface LandingTartalom {
  cimsor?: string;
  alcim?: string;
  bemutatkozas?: string;
  boritoKepUrl?: string;
  kollegak?: LandingKollega[];
}
export type HalozatKulcs = 'duna_house' | 'otthon_centrum';
/** Hívható időablak: napkulcs 1–7 (ISO hét napja, 1 = hétfő), érték: ["HH:MM", "HH:MM"]; hiányzó nap = zárva. */
export type Munkaido = Record<string, [string, string]>;
export interface IrodaHivas { telefon: string; munkaido: Munkaido }
export interface IrodaPublikus {
  slug: string; nev: string; logoUrl: string | null; slaPerc: number; landing: LandingTartalom;
  halozat: HalozatKulcs | null; hivas: IrodaHivas | null;
}

const szoveg = (v: unknown, max: number): string | undefined =>
  typeof v === 'string' && v.trim() !== '' ? v.trim().slice(0, max) : undefined;

export function httpsUrl(v: unknown): string | undefined {
  if (typeof v !== 'string') return undefined;
  try {
    const u = new URL(v);
    return u.protocol === 'https:' ? u.toString() : undefined;
  } catch {
    // Érvénytelen URL: szándékosan kihagyjuk; a landing kép nélkül is megjelenik.
    return undefined;
  }
}

function tomor<T extends object>(o: T): T {
  return Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined)) as T;
}

/**
 * A `landing_tartalom` jsonb szűrése a publikus kiadás előtt: csak az ismert kulcsok, hosszkorláttal,
 * csak https URL. Így ismeretlen vagy belső mező (pl. hálózat, mérési azonosító) nem kerülhet ki.
 */
export function landingTisztitas(nyers: unknown): LandingTartalom {
  const o = nyers && typeof nyers === 'object' && !Array.isArray(nyers) ? (nyers as Record<string, unknown>) : {};
  const kollegak = (Array.isArray(o.kollegak) ? o.kollegak : [])
    .flatMap((k): LandingKollega[] => {
      if (!k || typeof k !== 'object') return [];
      const r = k as Record<string, unknown>;
      const nev = szoveg(r.nev, 80);
      return nev ? [tomor({ nev, szerep: szoveg(r.szerep, 80), fotoUrl: httpsUrl(r.fotoUrl) })] : [];
    })
    .slice(0, 6);
  return tomor({
    cimsor: szoveg(o.cimsor, 120),
    alcim: szoveg(o.alcim, 300),
    bemutatkozas: szoveg(o.bemutatkozas, 1200),
    boritoKepUrl: httpsUrl(o.boritoKepUrl),
    kollegak: kollegak.length > 0 ? kollegak : undefined,
  });
}

export function halozatKulcs(v: unknown): HalozatKulcs | null {
  return v === 'duna_house' || v === 'otthon_centrum' ? v : null;
}

const IDO = /^([01]\d|2[0-3]):[0-5]\d$/;

/** Csak érvényes napkulcs (1–7) és "HH:MM" pár marad, a kezdet a vég előtt; a hibás nap kimarad. */
export function munkaidoTisztitas(nyers: unknown): Munkaido {
  const o = nyers && typeof nyers === 'object' && !Array.isArray(nyers) ? (nyers as Record<string, unknown>) : {};
  const ki: Munkaido = {};
  for (const [nap, par] of Object.entries(o)) {
    if (!/^[1-7]$/.test(nap) || !Array.isArray(par) || par.length !== 2) continue;
    const [tol, ig] = par;
    if (typeof tol === 'string' && typeof ig === 'string' && IDO.test(tol) && IDO.test(ig) && tol < ig) ki[nap] = [tol, ig];
  }
  return ki;
}

/** A „Hívjon most” adatai; null, ha nincs érvényes +36-os E.164 szám. A telefon csak ezen az úton kerülhet ki. */
export function hivasTisztitas(telefon: unknown, munkaido: unknown): IrodaHivas | null {
  if (typeof telefon !== 'string' || !/^\+36\d{8,9}$/.test(telefon)) return null;
  return { telefon, munkaido: munkaidoTisztitas(munkaido) };
}

/**
 * A web felső szintű útvonalai, amelyek nem lehetnek irodai slugok (az iroda oldala elérhetetlen lenne). Az adatbázis
 * `offices_slug_foglalt` megkötése ugyanezt a listát tartalmazza; új felső szintű oldalnál mindkettő bővül.
 */
export const FOGLALT_SLUGOK: readonly string[] = ['adatvedelem', 'sutik', 'felhasznalasi-feltetelek', 'partner-aszf', 'kepek', 'video', 'halozatok', '404', 'api', 'visszajelzes'];
